import { ArrowLeftOutlined,CalendarOutlined,CheckCircleOutlined,ClockCircleOutlined,EnvironmentOutlined } from '@ant-design/icons';
import { Collapse,Divider,Skeleton,Tag } from "antd";
import { getJobDetailPublicApi,getJobListPublicApi } from "api/services/jobs";
import { Button } from 'components/Button';
import { isValid,parse,parseISO } from 'date-fns';
import { useCallback,useEffect,useMemo,useState } from "react";
import { MdKeyboardArrowRight } from 'react-icons/md';
import { useLocation,useNavigate,useParams } from 'react-router-dom';
import { dateLeftHandler,formatDateHandler } from "utils/common";

const JobView = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams(); // Extracts the 'id' parameter from the URL

  const [jobDetail, setJobDetail] = useState({});
  const [loading, setLoading] = useState(false);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loadingSimilarJobs, setLoadingSimilarJobs] = useState(false);

  useEffect(() => {
    setLoading(true);
    getJobDetailPublicApi(id, {}).then(response => {
      setJobDetail(response.data);
      setLoading(false);
      
      // After getting job details, fetch similar jobs
      fetchSimilarJobs(response.data);
    }).catch(error => {
      console.error("Error fetching job details:", error);
      setLoading(false);
    });
  }, [id]);
  
  // Fetch similar jobs based on department or other criteria
  const fetchSimilarJobs = (currentJob) => {
    setLoadingSimilarJobs(true);
    getJobListPublicApi().then(response => {
      const allJobs = response.data.results || [];
      
      // Filter out the current job and limit to 3 jobs
      let filtered = allJobs
        .filter(job => job.id !== parseInt(id))
        .slice(0, 3);
        
      // If we have the current job's department, prioritize jobs from the same department
      if (currentJob?.department?.id) {
        // Sort jobs so that same department jobs come first
        filtered = allJobs
          .filter(job => job.id !== parseInt(id))
          .sort((a, b) => {
            // Sort by department match first
            if (a.department?.id === currentJob.department.id && b.department?.id !== currentJob.department.id) {
              return -1;
            }
            if (a.department?.id !== currentJob.department.id && b.department?.id === currentJob.department.id) {
              return 1;
            }
            // Then by recency (assuming newer jobs have higher IDs)
            return b.id - a.id;
          })
          .slice(0, 3);
      }
      
      setSimilarJobs(filtered);
      setLoadingSimilarJobs(false);
    }).catch(error => {
      console.error("Error fetching similar jobs:", error);
      setLoadingSimilarJobs(false);
    });
  };

  const handleBackToJobs = () => {
    navigate('/job-view');
  };

  const navigateToApplicationForm = () => {
    // Don't navigate if application is expired
    if (isApplicationExpired) {
      return;
    }
    // Use slug if available, otherwise use ID
    const jobIdentifier = jobDetail?.slug || id;
    navigate(`/job-apply/${jobIdentifier}`);
  };

  // Parse date with multiple format support
  const parseExpiryDate = useCallback((dateString) => {
    if (!dateString) return null;
    
    // Try ISO format first
    try {
      const isoDate = parseISO(dateString);
      if (isValid(isoDate)) return isoDate;
    } catch (e) {}
    
    // Try MM/dd/yyyy format
    try {
      const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date());
      if (isValid(parsedDate)) return parsedDate;
    } catch (e) {}
    
    // Try dd/MM/yyyy format
    try {
      const parsedDate = parse(dateString, 'dd/MM/yyyy', new Date());
      if (isValid(parsedDate)) return parsedDate;
    } catch (e) {}
    
    // Try standard Date constructor as fallback
    try {
      const date = new Date(dateString);
      if (isValid(date)) return date;
    } catch (e) {}
    
    return null;
  }, []);

  // Check if application is expired
  const isApplicationExpired = useMemo(() => {
    if (!jobDetail?.expiry_date) return false;
    
    const expiryDate = parseExpiryDate(jobDetail.expiry_date);
    if (!expiryDate) return false;
    
    // Compare dates - set time to start of day for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    
    // Check if expiry date is before today
    return expiry.getTime() < today.getTime();
  }, [jobDetail?.expiry_date, parseExpiryDate]);

  // Get days left text (never show negative)
  const getDaysLeftText = useMemo(() => {
    if (!jobDetail?.expiry_date) return null;
    
    const expiryDate = parseExpiryDate(jobDetail.expiry_date);
    if (!expiryDate) return "Application ends soon";
    
    // Compare dates - set time to start of day for accurate comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    
    // Check if expired first - if expiry is before today, it's expired
    if (expiry.getTime() < today.getTime()) {
      return "Application Closed";
    }
    
    // Only calculate days left if not expired
    const daysLeft = dateLeftHandler(jobDetail.expiry_date);
    return daysLeft ? `${daysLeft} left` : "Closing soon";
  }, [jobDetail?.expiry_date, parseExpiryDate]);

  const renderJobRequirements = () => {
    if (!jobDetail?.requirements) return null;
    
    let requirements = [];
    try {
      if (typeof jobDetail.requirements === 'string') {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(jobDetail.requirements);
        requirements = Array.isArray(parsed) ? parsed : [jobDetail.requirements];
      } else if (Array.isArray(jobDetail.requirements)) {
        requirements = jobDetail.requirements;
      } else {
        requirements = [jobDetail.requirements];
      }
    } catch (e) {
      requirements = [jobDetail.requirements];
    }
    
    return requirements.map((req, index) => (
      <li key={index} className="requirement-item">
        <CheckCircleOutlined className="me-2 nandos-text" />
        {req}
      </li>
    ));
  };

  const renderJobResponsibilities = () => {
    if (!jobDetail?.responsibilities) return null;
    
    let responsibilities = [];
    try {
      if (typeof jobDetail.responsibilities === 'string') {
        // Try to parse if it's a JSON string
        const parsed = JSON.parse(jobDetail.responsibilities);
        responsibilities = Array.isArray(parsed) ? parsed : [jobDetail.responsibilities];
      } else if (Array.isArray(jobDetail.responsibilities)) {
        responsibilities = jobDetail.responsibilities;
      } else {
        responsibilities = [jobDetail.responsibilities];
      }
    } catch (e) {
      responsibilities = [jobDetail.responsibilities];
    }
    
    return responsibilities.map((resp, index) => (
      <li key={index} className="responsibility-item">
        <CheckCircleOutlined className="me-2 nandos-text" />
        {resp}
      </li>
    ));
  };
 
  const LoadingState = () => {
    if (loading) {
      return (
        <div className="job-detail-skeleton">
          <div className="row">
            <div className="col-md-8">
              <Skeleton active paragraph={{ rows: 1 }} className="mb-3" />
              <Skeleton active paragraph={{ rows: 6 }} />
            </div>
            <div className="col-md-4">
              <Skeleton.Button active className="w-100 mb-3" style={{ height: '45px' }} />
              <Skeleton active paragraph={{ rows: 4 }} />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="main-wrapper">
        <div className="page-wrapper job-wrapper">
          <div className="content container">
            {/* Hero Section with Job Title */}
            <div className="nandos-hero p-4 p-md-5 rounded-3 mb-4">
              <div className="row">
                <div className="col-lg-8 mx-auto text-center">
                  <div className="nandos-logo-large mb-4">
                    <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid" style={{ maxHeight: '120px' }} />
                  </div>
                  {loading ? (
                    <Skeleton.Input active style={{ width: '60%' }} />
                  ) : (
                    <h1 className="display-6 fw-bold mb-3 text-white">{jobDetail?.title || "Job Details"}</h1>
                  )}
                  {loading ? (
                    <Skeleton.Input active style={{ width: '40%' }} />
                  ) : (
                    <p className="lead mb-0 text-white">
                      {/* Department name hidden as requested */}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Back to Jobs Button */}
            <div className="mb-4">
              <Button
                label={<><ArrowLeftOutlined /> Back to Jobs</>}
                className="btn btn-outline-secondary"
                onClick={handleBackToJobs}
              />
            </div>
            
            <LoadingState />
            
            {!loading && (
              <div className="row">
                <div className="col-lg-8">
                  {/* Job Information Card */}
                  <div className="card job-detail-card mb-4">
                    <div className="nandos-card-accent"></div>
                    <div className="card-body p-4">
                      {/* Job Meta Information */}
                      <div className="job-meta-info mb-4">
                        <div className="d-flex flex-wrap gap-4 mb-3">
                          <div className="d-flex align-items-center">
                            <CalendarOutlined className="me-2 nandos-text" />
                            <div>
                              <div className="text-muted small">Posted</div>
                              <div>{formatDateHandler(jobDetail?.created_at)}</div>
                            </div>
                          </div>
                          
                          {jobDetail?.expiry_date && (
                            <div className="d-flex align-items-center">
                              <ClockCircleOutlined className="me-2 nandos-text" />
                              <div>
                                <div className="text-muted small">Closing Date</div>
                                <div>{formatDateHandler(jobDetail?.expiry_date)}</div>
                              </div>
                            </div>
                          )}
                          
                          {/* <div className="d-flex align-items-center">
                            <UserOutlined className="me-2 nandos-text" />
                            <div>
                              <div className="text-muted small">Vacancies</div>
                              <div>{jobDetail?.application_count || 1}</div>
                            </div>
                          </div> */}
                          
                          {jobDetail?.location && (
                            <div className="d-flex align-items-center">
                              <EnvironmentOutlined className="me-2 nandos-text" />
                              <div>
                                <div className="text-muted small">Location</div>
                                <div>{jobDetail.location}</div>
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <Tag color="#f8c429">{jobDetail?.department?.name}</Tag>
                          <Tag color="#4a8501">Full Time</Tag>
                          {jobDetail?.tags && jobDetail.tags.map((tag, i) => (
                            <Tag key={i} color="#a61022">{tag}</Tag>
                          ))}
                        </div>
                      </div>
                      
                      <Divider />
                      
                      <Collapse 
                        defaultActiveKey={['1']} 
                        expandIcon={({ isActive }) => <MdKeyboardArrowRight size={28} className={`rotate-icon ${isActive ? 'active' : ''}`} />}
                        className="job-details-accordion"
                        style={{ width: '100%' }}
                      >
                        {/* Job Description */}
                        <Collapse.Panel header="About This Role" key="1" className="job-description">
                          <div className="description-content">
                            {jobDetail?.description?.split('\n').map((paragraph, index) => (
                              <p key={index}>{paragraph}</p>
                            ))}
                          </div>
                        </Collapse.Panel>
                        
                        {/* Job Requirements */}
                        {jobDetail?.requirements && (
                          <Collapse.Panel header="Requirements" key="2" className="job-requirements">
                            <ul className="requirements-list">
                              {renderJobRequirements()}
                            </ul>
                          </Collapse.Panel>
                        )}
                        
                        {/* Job Responsibilities */}
                        {jobDetail?.responsibilities && (
                          <Collapse.Panel header="Key Responsibilities" key="3" className="job-responsibilities">
                            <ul className="responsibilities-list">
                              {renderJobResponsibilities()}
                            </ul>
                          </Collapse.Panel>
                        )}
                        
                        {/* Additional Information */}
                        {jobDetail?.additional_info && (
                          <Collapse.Panel header="Additional Information" key="4" className="additional-info">
                            {jobDetail.additional_info.split('\n').map((paragraph, index) => (
                              <p key={index}>{paragraph}</p>
                            ))}
                          </Collapse.Panel>
                        )}
                      </Collapse>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4">
                  {/* Application Card */}
                  <div className="card application-card sticky-top" style={{ top: '20px' }}>
                    <div className="nandos-card-accent"></div>
                    <div className="card-body p-4">
                      <div className="text-center mb-4">
                        <div className="company-logo mb-3">
                          <div className="nandos-logo-container rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: '70px', height: '70px' }}>
                            {/* <FireOutlined className="nandos-icon" style={{ fontSize: '32px' }} /> */}
                            <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid contain" style={{ height: 50,}} />
                          </div>
                        </div>
                        <h5 className="mb-1">Nando's</h5>
                        <p className="text-muted mb-0">Join our team!</p>
                      </div>
                      
                      {!isApplicationExpired && (
                        <Button
                          label="Apply For This Position"
                          className="btn nandos-btn w-100 mb-3"
                          onClick={navigateToApplicationForm}
                        />
                      )}
                      
                      {jobDetail?.expiry_date && (
                        <div className={`deadline-alert text-center mt-3 ${isApplicationExpired ? 'text-danger' : ''}`}>
                          <ClockCircleOutlined className="me-2" style={{ color: isApplicationExpired ? '#dc3545' : '#a61022' }} />
                          <span className="fw-medium">
                            {getDaysLeftText}
                          </span>
                        </div>
                      )}
                      
                      {/* Benefits Section */}
                      {/* <div className="benefits-section mt-4">
                        <h5 className="text-center mb-3">Benefits</h5>
                        <ul className="benefits-list">
                          <li><CheckCircleOutlined className="me-2 nandos-text" /> Competitive salary</li>
                          <li><CheckCircleOutlined className="me-2 nandos-text" /> Staff meal on shift</li>
                          <li><CheckCircleOutlined className="me-2 nandos-text" /> Flexible working hours</li>
                          <li><CheckCircleOutlined className="me-2 nandos-text" /> Career development</li>
                          <li><CheckCircleOutlined className="me-2 nandos-text" /> Friendly work environment</li>
                        </ul>
                      </div> */}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Similar Jobs Section */}
            {!loading && (
              <div className="similar-jobs-section mt-5">
                <h3 className="mb-4">You might also be interested in</h3>
                <div className="row">
                  {loadingSimilarJobs ? (
                    <div className="col-lg-4 col-md-6 mb-4">
                      <Skeleton active paragraph={{ rows: 1 }} />
                    </div>
                  ) : similarJobs.length === 0 ? (
                    <div className="col-lg-4 col-md-6 mb-4">
                      <p>No similar jobs found at this time.</p>
                    </div>
                  ) : (
                    similarJobs.map((job, index) => (
                      <div className="col-lg-4 col-md-6 mb-4" key={job.id}>
                        <div className="card job-card h-100 shadow-sm hover-shadow">
                          <div className="nandos-card-accent"></div>
                          <div className="card-body p-4">
                            <div className="d-flex align-items-center mb-3">
                              <div className="company-logo me-3">
                                <div className="nandos-logo-container rounded d-flex align-items-center justify-content-center">
                                  {/* <FireOutlined className="nandos-icon" /> */}
                                  <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid contain" style={{ height: 50,}} />

                                </div>
                              </div>
                              <div>
                                <h5 className="job-title fw-bold mb-1">
                                  {job.title}
                                </h5>
                                <p className="company-name mb-0">Nando's {job.location}</p>
                              </div>
                            </div>
                            <div className="d-flex justify-content-end">
                              <Button
                                label="View Job"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => navigate(`/job-view/${job.id}`)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="nandos-footer p-4 mt-5 text-center">
            <div className="container">
              <div className="row">
                <div className="col-12">
                  <img src={require("../../../../../../assets/img/logo3.png")} alt="Nando's" className="img-fluid mb-3" style={{ maxHeight: '60px' }} />
                  <p className="mb-0">© {new Date().getFullYear()} Nando's. All rights reserved.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Apply Job Modal */}
      
      {/* Add custom CSS */}
      <style jsx>{`
        .job-wrapper {
          background-color: #f8f9fa;
          min-height: 100vh;
        }
        
        .nandos-hero {
          background: linear-gradient(135deg, #a61022 0%, #7a0c19 100%);
          color: white;
        }
        
        .job-detail-card, .application-card {
          border: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          position: relative;
          overflow: hidden;
        }
        
        .nandos-card-accent {
          height: 5px;
          background: linear-gradient(90deg, #a61022 0%, #f8c429 100%);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
        }
        
        .section-title {
          color: #333;
          font-size: 1rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .nandos-logo-container {
          width: 50px;
          height: 50px;
          background-color: #a61022;
          color: #fff;
        }
        
        .nandos-icon {
          font-size: 24px;
          color: #f8c429;
        }
        
        .nandos-text {
          color: #a61022;
        }
        
        .nandos-btn {
          background-color: #a61022;
          border-color: #a61022;
          color: white;
        }
        
        .nandos-btn:hover {
          background-color: #7a0c19;
          border-color: #7a0c19;
          color: white;
        }
        
        .department-badge {
          background-color: rgba(255, 255, 255, 0.2);
          padding: 5px 15px;
          border-radius: 20px;
          font-weight: 500;
        }
        
        .benefits-list {
          list-style: none;
          padding-left: 0;
        }
        
        .benefits-list li {
          margin-bottom: 8px;
        }
        
        .requirements-list, .responsibilities-list {
          list-style: none;
          padding-left: 0;
        }
        
        .requirement-item, .responsibility-item {
          margin-bottom: 12px;
          display: flex;
          align-items: flex-start;
        }
        
        .deadline-alert {
          color: #a61022;
          font-size: 0.9rem;
        }
        
        .hover-shadow:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
          transition: all 0.3s ease;
        }
        
        .nandos-footer {
          background-color: #333;
          color: white;
        }
        
        .description-content p, .additional-info p {
          margin-bottom: 0.75rem;
        }
        
        .description-content p:empty, .additional-info p:empty {
          margin-bottom: 1rem;
        }
        

        .job-details-accordion {
          margin-bottom: 2rem;
        }
        
        .job-details-accordion .ant-collapse-header {
          font-weight: 600;
          font-size: 1.1rem;
          // color: #333;
          padding: 16px 20px;
        }
        
        .job-details-accordion .ant-collapse-content-box {
          padding: 20px 24px;
          background-color: #fafafa;
        }
        
        .job-details-accordion .ant-collapse-item {
          margin-bottom: 0.75rem;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #eaeaea;
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
        }
        
      
        .rotate-icon {
          transition: transform 0.3s ease;
          font-size: 28px;
        }

        .rotate-icon.active {
          transform: rotate(90deg);
        }
        
        /* Fix for right-aligning the collapse icon */
        .job-details-accordion .ant-collapse-header {
          display: flex !important;
          flex-direction: row-reverse !important;
          justify-content: flex-start !important;
          font-weight: 600;
          font-size: 1.1rem;
          padding: 16px 20px;
        }
        
        .job-details-accordion .ant-collapse-header-text {
          font-size: 1.25rem;
          font-weight: 700;
          margin-right: auto;
        }
        
        .job-details-accordion .ant-collapse-expand-icon {
          padding-right: 0 !important;
          padding-left: 8px !important;
          margin: 0 !important;
        }
      `}</style>
    </div>
  );
};

export default JobView;
